import { track, trackingAllowed } from './analytics';

/**
 * Where a visitor came from, when it was an AI assistant: ChatGPT, Claude, Perplexity and the rest send people to the ordering site from
 * a chat (the browser says which in the referrer, and ChatGPT also adds utm_source=chatgpt.com to the address). That is told to Google
 * Analytics as an `ai_referral` event with the `ai_platform`, so it can be a channel of its own (see docs/ai-referrals.md).
 *
 * Nothing is kept beyond the session, and nothing is done for a visitor who asked not to be tracked.
 */

/** The assistants that send people here: the domain they come from, and the name it is reported under. */
export const AI_SOURCES: Record<string, string> = {
    'chatgpt.com': 'ChatGPT',
    'chat.openai.com': 'ChatGPT',
    'claude.ai': 'Claude',
    'perplexity.ai': 'Perplexity',
    'gemini.google.com': 'Gemini',
    'bard.google.com': 'Gemini',
    'copilot.microsoft.com': 'Copilot',
    'meta.ai': 'Meta AI',
    'grok.com': 'Grok',
    'chat.mistral.ai': 'Mistral',
    'chat.deepseek.com': 'DeepSeek',
    'you.com': 'You.com',
    'poe.com': 'Poe',
    'phind.com': 'Phind',
};

const STORAGE_KEY = 'ai_referral';
const REPORTED_KEY = 'ai_referral_reported';

const hostOf = (value: string): string | null => {
    try {
        return new URL(value).hostname.toLowerCase().replace(/^www\./, '');
    } catch {
        return null;
    }
};

const platformForHost = (host: string): string | null => {
    for (const domain of Object.keys(AI_SOURCES)) {
        if (host === domain || host.endsWith('.' + domain)) return AI_SOURCES[domain];
    }

    return null;
};

/** The assistant a visit came from, if it did: from the referrer, else from utm_source. */
export const detectAiReferral = (referrer: string, search: string): string | null => {
    const host = referrer ? hostOf(referrer) : null;

    if (host) {
        const fromReferrer = platformForHost(host);
        if (fromReferrer) return fromReferrer;
    }

    // (an address with utm_source=chatgpt.com, or utm_source=claude: the domain or the name of the assistant)
    const source = new URLSearchParams(search).get('utm_source')?.trim().toLowerCase().replace(/^www\./, '');

    if (!source) return null;

    for (const [domain, name] of Object.entries(AI_SOURCES)) {
        if (source === domain || source === name.toLowerCase() || source === domain.split('.')[0]) return name;
    }

    return null;
};

const doNotTrack = (): boolean => !trackingAllowed();

/** The assistant this visit came from, once it has been noticed (kept for the session, as the visitor moves between pages). */
export const getAiReferral = (): string | null => {
    try {
        return sessionStorage.getItem(STORAGE_KEY);
    } catch {
        return null;
    }
};

/** Notices, on the first page of a visit, that it came from an assistant. Returns the assistant, or null. */
export const captureAiReferral = (): string | null => {
    if (doNotTrack()) return null;

    try {
        const known = sessionStorage.getItem(STORAGE_KEY);
        if (known) return known;

        const platform = detectAiReferral(document.referrer, window.location.search);
        if (platform) sessionStorage.setItem(STORAGE_KEY, platform);

        return platform;
    } catch {
        return null;
    }
};

/** Tells Google Analytics (Orderswift's property, and the operator's when it is known), once for a visit. */
export const reportAiReferral = (platform: string | null): void => {
    if (!platform) return;

    try {
        if (sessionStorage.getItem(REPORTED_KEY)) return;
        sessionStorage.setItem(REPORTED_KEY, '1');
    } catch {
        // (storage is off: report it anyway rather than never)
    }

    if (!trackingAllowed()) return;

    const gtag = (window as any).gtag;

    if (typeof gtag === 'function') {
        gtag('set', 'user_properties', { ai_platform: platform });
    }

    track('ai_referral', { ai_platform: platform });
};
